import React, { useEffect, useMemo, useState } from "react";

function getParam(params, key) {
  try {
    const v = params.get(key);
    return v == null ? "" : String(v);
  } catch {
    return "";
  }
}

export default function Welcome({ onGoHome, onGoLogin }) {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const verified = getParam(params, "verified");
  const reason = getParam(params, "reason");
  const created = getParam(params, "created");
  const verifyUrl = getParam(params, "verifyUrl");

  const [copied, setCopied] = useState(false);

  const { title, message, showVerifyBox, showLoginBtn, autoTarget } = useMemo(() => {
    if (verified === "1") {
      return {
        title: "Email verified successfully",
        message: "You may now log in. Redirecting you to the login page…",
        showVerifyBox: false,
        showLoginBtn: true,
        autoTarget: "login",
      };
    }

    if (verified === "0") {
      const msg =
        reason === "expired"
          ? "That verification link expired. Please request a new one from the login page."
          : reason === "missing_token"
            ? "Missing verification token. Please request a new link from the login page."
            : "That verification link is invalid or already used. Please request a new one from the login page.";

      return {
        title: "Verification issue",
        message: msg,
        showVerifyBox: false,
        showLoginBtn: true,
        autoTarget: "login",
      };
    }

    if (created === "1") {
      return {
        title: "Account created successfully",
        message:
          "Next step: verify your email using the link below, then return and log in.",
        showVerifyBox: Boolean(verifyUrl),
        showLoginBtn: true,
        autoTarget: "home",
      };
    }

    return {
      title: "Welcome to NextScenes",
      message: "Redirecting you to the homepage…",
      showVerifyBox: false,
      showLoginBtn: false,
      autoTarget: "home",
    };
  }, [verified, reason, created, verifyUrl]);

  async function copyLink() {
    setCopied(false);
    try {
      await navigator.clipboard.writeText(String(verifyUrl || ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  }

  useEffect(() => {
    // Timing rules:
    // - Verified: send to Login quickly (user’s next action is login)
    // - Created with verifyUrl: give enough time to copy/open the link
    // - Otherwise: short redirect to Home

    let ms = 3500;

    if (verified === "1") ms = 2500;
    if (verified === "0") ms = 4500;

    if (created === "1" && verifyUrl) ms = 20000;

    const t = setTimeout(() => {
      if (autoTarget === "login") {
        if (typeof onGoLogin === "function") onGoLogin();
        else window.location.href = "/login";
        return;
      }

      if (typeof onGoHome === "function") onGoHome();
      else window.location.href = "/";
    }, ms);

    return () => clearTimeout(t);
  }, [onGoHome, onGoLogin, created, verifyUrl, verified, autoTarget]);

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 720, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: "#334155" }}>
          {message}
        </p>

        {showVerifyBox ? (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#f8fafc",
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 8 }}>
              Verification Link
            </div>

            <div
              style={{
                padding: 10,
                background: "white",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                wordBreak: "break-all",
              }}
            >
              <a href={verifyUrl} target="_blank" rel="noreferrer">
                {verifyUrl}
              </a>
            </div>

            <div
              style={{
                marginTop: 10,
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button className="btn" type="button" onClick={copyLink}>
                {copied ? "Copied ✔" : "Copy link"}
              </button>

              <button
                className="btn"
                type="button"
                onClick={() =>
                  typeof onGoLogin === "function"
                    ? onGoLogin()
                    : (window.location.href = "/login")
                }
              >
                Go to Login
              </button>
            </div>
          </div>
        ) : null}

        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="btn"
            type="button"
            onClick={() =>
              typeof onGoHome === "function"
                ? onGoHome()
                : (window.location.href = "/")
            }
          >
            Close
          </button>

          {showLoginBtn ? (
            <button
              className="btn"
              type="button"
              onClick={() =>
                typeof onGoLogin === "function"
                  ? onGoLogin()
                  : (window.location.href = "/login")
              }
            >
              Go to Login
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
